<template>
  <dialog
    class="JoinCrewModalView"
    data-theme="light"
    :aria-label="`Request to join ${crew?.name || 'crew'}`"
    :aria-busy="isFetchingCrew"
    open
  >
    <div
      class="JoinCrewModalView__backdrop"
      data-testid="backdrop"
      @click="closeActiveModal()"
    />
    <Transition>
      <div
        v-if="crew"
        class="JoinCrewModalView__windowContainer"
      >
        <section
          class="JoinCrewModalView__window"
        >
          <header class="JoinCrewModalView__header">
            <h1 class="JoinCrewModalView__title">
              {{ crew.name }}
            </h1>
            <div
              class="JoinCrewModalView__meta"
              data-testid="crewMeta"
            >
              {{ formattedCount }}
            </div>
          </header>
          <PlayerAvatarList
            v-model="players"
            class="JoinCrewModalView__playerList"
            justify="center"
          />
          <FormButton
            :disabled="isRequestingToJoin || hasRequestedToJoin || isPlayerInCrew || isRequestPending"
            @click="handleRequestClick"
          >
            <span v-if="isRequestingToJoin">
              Requesting to join…
            </span>
            <span v-else-if="hasRequestedToJoin">
              Request sent!
            </span>
            <span v-else-if="isPlayerInCrew">
              Already a member
            </span>
            <span v-else-if="isRequestPending">
              Request pending
            </span>
            <span v-else>
              Request to join
            </span>
          </FormButton>
        </section>
      </div>
    </Transition>
  </dialog>
</template>

<script lang="ts" setup>
import FormButton from "@/components/FormButton.vue";
import PlayerAvatarList from "@/components/PlayerAvatarList.vue";
import useRealtimeChannel from "@/composables/useRealtimeChannel";
import { supabaseClient } from "@/lib/database/supabase";
import useAuthStore from "@/stores/useAuthStore";
import useModalStore from "@/stores/useModalStore";
import { CrewRequest, CrewWithPlayers, CrewWithRequests } from "@shared/lib/database/query.types";
import { RealtimePostgresUpdatePayload } from "@supabase/supabase-js";
import { storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";

const {
  crewId,
} = defineProps<{
  crewId: string,
}>();

const {
  currentPlayer,
} = storeToRefs(useAuthStore());

const {
  closeActiveModal,
} = useModalStore();

useRealtimeChannel("crew_requests", {
  handleUpdate: handleCrewRequestUpdate,
});

const isFetchingCrew = ref(true);
const isRequestingToJoin = ref(false);
const hasRequestedToJoin = ref(false);
const abortController = ref<AbortController>();

const crew = ref<CrewWithPlayers & CrewWithRequests>();

watch(() => crewId, async (newCrewId) => {
  isFetchingCrew.value = true;
  abortController.value?.abort();
  abortController.value = new AbortController();

  const {
    data: _crew,
    error,
  } = await supabaseClient.from("crews")
    .select("*, players(*), crew_requests(*)")
    .abortSignal(abortController.value.signal)
    .eq("id", newCrewId)
    .limit(1)
    .maybeSingle();

  if (_crew) {
    crew.value = _crew;
  } else if (error) {
    alert(error.message);
  }

  isFetchingCrew.value = false;
}, {
  immediate: true,
});

const players = computed(() => crew.value?.players || []);

const formattedCount = computed(() => {
  return `${players.value.length} ${players.value.length !== 1 ? "players" : "player"}`;
});

const isPlayerInCrew = computed(() => {
  return !!players.value.find((player) => player.id === currentPlayer.value?.id);
});

const isRequestPending = computed(() => {
  return !!crew.value?.crew_requests.find((crewRequest) => crewRequest.player_id === currentPlayer.value?.id);
});

async function handleCrewRequestUpdate (payload: RealtimePostgresUpdatePayload<CrewRequest>) {
  const crewRequest = payload.new;

  if (crewRequest.player_id === currentPlayer.value?.id && crewRequest.accepted_at) {
    closeActiveModal();
  }
}

async function handleRequestClick () {
  if (!currentPlayer.value) return;

  isRequestingToJoin.value = true;
  const {
    error,
  } = await supabaseClient.from("crew_requests").insert({
    crew_id: crewId,
    player_id: currentPlayer.value.id,
  });
  isRequestingToJoin.value = false;

  if (!error) {
    hasRequestedToJoin.value = true;
  } else {
    alert(error.message);
  }
}
</script>

<style lang="scss" scoped>
.JoinCrewModalView {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999;
  isolation: isolate;

  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  padding: 24px 16px;

  transition: none 350ms;
}

.JoinCrewModalView__backdrop {
  position: absolute;
  inset: 0;
  z-index: -1;

  background-color: var(--themeColorBackdrop);
  backdrop-filter: blur(6px);

  cursor: pointer;

  transition: opacity 350ms var(--easeOutBack);

  .v-leave-active & {
    transition: 350ms;
    transition-timing-function: initial;
  }

  .v-enter-from &,
  .v-leave-to & {
    opacity: 0;
  }
}

.JoinCrewModalView__windowContainer {
  width: 100%;
}

.JoinCrewModalView__window {
  display: grid;
  padding: 32px 16px 16px;

  border-radius: 16px;
  background-color: var(--themeColorNeutral0);

  text-align: center;

  .v-enter-active & {
    transition: 335ms cubic-bezier(0.25, 1, 0.30, 1);
    transition-property: opacity, transform;
  }

  .v-leave-active & {
    transition: 350ms;
    transition-property: opacity;
  }

  .v-enter-from & {
    transform: scale(0.8);
    opacity: 0;
  }

  .v-leave-to & {
    opacity: 0;
  }
}

.JoinCrewModalView__header {
  display: grid;
  gap: 8px;
}

.JoinCrewModalView__title {
  font: var(--fontWeightMedium) 24px / 1.2 var(--fontFamilyNormal);
  font-stretch: var(--fontWidthExtended);
}

.JoinCrewModalView__meta {
  font: var(--fontWeightNormal) 18px / 1 var(--fontFamilyNormal);
  font-stretch: var(--fontWidthExtended);
}

.JoinCrewModalView__playerList {
  margin: 26px 0 32px;
}
</style>
